import React, { FC } from "react";

/**
 * Small dependency-free charts for AI reporting (Ask answers and AI-created reports):
 * horizontal bars, vertical columns (grouped or stacked) and a line chart.
 */

export const SERIES_COLORS = [
  "#6B4EFF",
  "#B9AAFF",
  "#12805C",
  "#E08A1E",
  "#3B6FE0",
  "#B42318",
];

export type ChartPoint = {
  key: string;
  label: string;
  values: (number | null)[];
};

type ChartProps = {
  kind: "bar" | "stacked_bar" | "line";
  series: string[];
  points: ChartPoint[];
  format: (value: number) => string;
  /** Horizontal bars (labels on the left), used in the Ask panel. */
  horizontal?: boolean;
  colors?: string[];
};

const num = (v: number | null | undefined) =>
  v !== null && v !== undefined && Number.isFinite(v) ? v : 0;

export const ChartLegend: FC<{ series: string[]; colors?: string[] }> = ({
  series,
  colors = SERIES_COLORS,
}) =>
  series.length > 1 ? (
    <div
      className={
        "flex flex-wrap items-center gap-3.5 text-[11.5px] font-semibold text-[#6B7185]"
      }>
      {series.map((name, i) => (
        <span key={name} className={"inline-flex items-center gap-1.5"}>
          <i
            className={"inline-block h-2.5 w-2.5 rounded-[3px]"}
            style={{ background: colors[i % colors.length] }}
          />
          {name}
        </span>
      ))}
    </div>
  ) : null;

const HorizontalBars: FC<ChartProps> = ({
  kind,
  series,
  points,
  format,
  colors = SERIES_COLORS,
}) => {
  const stacked = kind === "stacked_bar";
  const max = Math.max(
    1e-9,
    ...points.map((p) =>
      stacked
        ? p.values.reduce<number>((s, v) => s + Math.max(0, num(v)), 0)
        : Math.max(...p.values.map(num)),
    ),
  );
  return (
    <div className={"flex flex-col gap-2"} data-testid={"chart-hbars"}>
      {points.map((p) => {
        const total = p.values.reduce<number>((s, v) => s + num(v), 0);
        return (
          <div
            key={p.key}
            className={"grid items-center gap-2.5"}
            style={{ gridTemplateColumns: "minmax(80px,140px) 1fr auto" }}>
            <span
              className={"truncate text-[12.5px] font-semibold text-[#3A4256]"}
              title={p.label}>
              {p.label}
            </span>
            {stacked ? (
              <span
                className={
                  "flex h-4 overflow-hidden rounded-[5px] bg-[#F1EEFF]"
                }>
                {p.values.map((v, i) => (
                  <span
                    key={i}
                    style={{
                      width: `${(Math.max(0, num(v)) / max) * 100}%`,
                      background: colors[i % colors.length],
                    }}
                  />
                ))}
              </span>
            ) : (
              <span className={"flex flex-col gap-0.5"}>
                {p.values.map((v, i) => (
                  <span
                    key={i}
                    className={
                      "h-3.5 overflow-hidden rounded-[5px] bg-[#F1EEFF]"
                    }>
                    <span
                      className={"block h-full rounded-[5px]"}
                      style={{
                        width: `${(Math.max(0, num(v)) / max) * 100}%`,
                        background: colors[i % colors.length],
                      }}
                    />
                  </span>
                ))}
              </span>
            )}
            <span
              className={"text-right text-[12.5px] font-bold text-[#1F2433]"}>
              {stacked || series.length === 1
                ? format(total)
                : p.values.map((v) => format(num(v))).join(" / ")}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const Columns: FC<ChartProps> = ({
  kind,
  points,
  format,
  colors = SERIES_COLORS,
}) => {
  const stacked = kind === "stacked_bar";
  const max = Math.max(
    1e-9,
    ...points.map((p) =>
      stacked
        ? p.values.reduce<number>((s, v) => s + Math.max(0, num(v)), 0)
        : Math.max(...p.values.map(num)),
    ),
  );
  const H = 170;
  const showValues = points.length <= 16;
  return (
    <div data-testid={"chart-columns"}>
      <div
        className={
          "flex h-[220px] items-end gap-2 border-b border-[#EDEFF3] pt-2.5 sm:gap-4"
        }>
        {points.map((p) => (
          <div
            key={p.key}
            className={
              "flex h-full min-w-0 flex-1 items-end justify-center gap-1"
            }>
            {stacked ? (
              <div className={"flex flex-col items-center gap-1"}>
                {showValues && (
                  <span className={"text-[11px] font-bold text-[#1F2433]"}>
                    {format(p.values.reduce<number>((s, v) => s + num(v), 0))}
                  </span>
                )}
                <div
                  className={
                    "flex w-7 flex-col-reverse overflow-hidden rounded-t-[5px] sm:w-9"
                  }>
                  {p.values.map((v, i) => (
                    <div
                      key={i}
                      style={{
                        height: (Math.max(0, num(v)) / max) * H,
                        background: colors[i % colors.length],
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              p.values.map((v, i) => (
                <div
                  key={i}
                  className={"flex min-w-0 flex-col items-center gap-1"}>
                  {showValues && (
                    <span
                      className={"whitespace-nowrap text-[11px] font-bold"}
                      style={{ color: i ? "#9097A8" : "#1F2433" }}>
                      {v === null ? "—" : format(num(v))}
                    </span>
                  )}
                  <div
                    className={"w-5 rounded-t-[5px] sm:w-7"}
                    style={{
                      height: Math.max(2, (Math.max(0, num(v)) / max) * H),
                      background: colors[i % colors.length],
                    }}
                  />
                </div>
              ))
            )}
          </div>
        ))}
      </div>
      <div className={"mt-2 flex gap-2 sm:gap-4"}>
        {points.map((p) => (
          <span
            key={p.key}
            className={
              "min-w-0 flex-1 truncate text-center text-xs font-semibold text-[#3A4256]"
            }
            title={p.label}>
            {p.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const Line: FC<ChartProps> = ({ points, format, colors = SERIES_COLORS }) => {
  const W = 640;
  const H = 200;
  const PAD = 28;
  const count = Math.max(1, points[0]?.values.length ?? 1);
  const all = points.flatMap((p) => p.values.map(num));
  const max = Math.max(1e-9, ...all);
  const x = (i: number) =>
    points.length <= 1
      ? W / 2
      : PAD + (i * (W - PAD * 2)) / (points.length - 1);
  const y = (v: number) => H - PAD - (v / max) * (H - PAD * 2);
  const every = Math.ceil(points.length / 12);
  return (
    <svg
      viewBox={`0 0 ${W} ${H + 18}`}
      className={"w-full"}
      role={"img"}
      data-testid={"chart-line"}>
      <line
        x1={PAD}
        x2={W - PAD}
        y1={H - PAD}
        y2={H - PAD}
        stroke={"#EDEFF3"}
      />
      {Array.from({ length: count }, (_, s) => (
        <g key={s}>
          <polyline
            fill={"none"}
            stroke={colors[s % colors.length]}
            strokeWidth={2.5}
            strokeLinejoin={"round"}
            points={points
              .map((p, i) => `${x(i)},${y(num(p.values[s]))}`)
              .join(" ")}
          />
          {points.map((p, i) => (
            <circle
              key={i}
              cx={x(i)}
              cy={y(num(p.values[s]))}
              r={3}
              fill={colors[s % colors.length]}>
              <title>{`${p.label}: ${format(num(p.values[s]))}`}</title>
            </circle>
          ))}
        </g>
      ))}
      <text x={PAD} y={14} fontSize={11} fill={"#9097A8"} fontWeight={600}>
        {format(max)}
      </text>
      {points.map((p, i) =>
        i % every === 0 ? (
          <text
            key={p.key}
            x={x(i)}
            y={H + 6}
            fontSize={11}
            fill={"#3A4256"}
            fontWeight={600}
            textAnchor={"middle"}>
            {p.label}
          </text>
        ) : null,
      )}
    </svg>
  );
};

export const ReportChart: FC<ChartProps> = (props) => {
  if (!props.points.length)
    return (
      <div className={"py-8 text-center text-sm font-semibold text-[#5B6472]"}>
        Nothing to show.
      </div>
    );
  if (props.kind === "line") return <Line {...props} />;
  return props.horizontal ? (
    <HorizontalBars {...props} />
  ) : (
    <Columns {...props} />
  );
};
